import React, { useContext } from 'react'
import { Flex, Box, Label, Checkbox, Text } from 'pcln-design-system'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { Discount } from 'pcln-icons'
import { ProductOption } from '../Flights/types'
import analytics from '../../utils/analytics'
import { SetiRedesignContext } from '../providers/RedesignProvider'

type BundleAndSaveType = {
  category: string
  productOptions: Readonly<ProductOption[]>
  tripType: string
  label: string
  setValue: (key: string, value: string) => void
  color?: string
  isStickySearch?: boolean
  height?: string
  isSubmitButtonReplacement?: boolean
}

const StyledText = styled(Text).attrs(() => ({
  pr: [1, 2, null, 3]
}))`
  white-space: nowrap;
`

const FlexWrapper = ({
  children,
  isStickySearch,
  height,
  isSubmitButtonReplacement
}: {
  children: React.ReactNode
  isStickySearch: boolean
  height?: string
  isSubmitButtonReplacement?: boolean
}) => {
  const { isHPCornerShadowColorRedesign, isHotelsCornerShadClrVariant } =
    useContext(SetiRedesignContext)
  const shouldUseBrandRefreshTheme =
    isHPCornerShadowColorRedesign || isHotelsCornerShadClrVariant
  const flexProps = !isStickySearch && {
    p: 1,
    mt: isSubmitButtonReplacement ? 0 : [0, 3, null, null, 0],
    mr: [2, 0],
    borderRadius: shouldUseBrandRefreshTheme ? 'lg' : '6px',
    height,
    color: 'highlight.light'
  }
  return (
    <Flex alignItems="center" {...flexProps}>
      {children}
    </Flex>
  )
}

export default function BundleAndSave({
  category,
  productOptions,
  tripType,
  label,
  setValue,
  color = 'gray',
  isStickySearch = false,
  height,
  isSubmitButtonReplacement
}: BundleAndSaveType) {
  const {
    query: { tab = 'hotels' },
    pathname
  } = useRouter()
  const isHomepage = pathname === '/'
  const parent = isHomepage ? `${tab}-` : ''

  return (
    <>
      <FlexWrapper
        isStickySearch={isStickySearch}
        height={height}
        isSubmitButtonReplacement={isSubmitButtonReplacement}
      >
        {isStickySearch ? (
          <StyledText fontSize={0} color={color}>
            {label}
          </StyledText>
        ) : (
          <Flex>
            <Discount
              m={1}
              mr={2}
              color={isHomepage ? '#017000' : 'success.dark'}
            />
            <StyledText
              color={isHomepage ? '#017000' : 'success.dark'}
              bold
              fontSize={[0, null, null, null, 1]}
              m="auto"
            >
              {label}
            </StyledText>
          </Flex>
        )}
        <Flex flexWrap="wrap" alignItems="center">
          {productOptions.map(option => {
            const checked = tripType.includes(option.type)
            return (
              <Box key={option.name} mt="2px" pr={[1, 2, 3]}>
                <Label fontSize={0} htmlFor={option.id} color={color}>
                  <Checkbox
                    aria-label={option.label}
                    id={option.id}
                    data-testid={option.id}
                    name={option.name}
                    value={option.type}
                    checked={checked}
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement>
                    ): void => {
                      let value
                      if (e.target.checked) {
                        value = `${tripType}:${e.target.value}`
                        analytics.fireEvent({
                          action: 'Add Product Checkbox',
                          category,
                          label: `${parent}${option.label}`
                        })
                      } else {
                        value = tripType.replace(`:${e.target.value}`, '')
                      }
                      setValue('tripType', value)
                    }}
                  />
                  {` ${option.label} `}
                </Label>
              </Box>
            )
          })}
        </Flex>
      </FlexWrapper>
    </>
  )
}
